import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getAppAnalyticsSnapshot } from '@/services/appAnalyticsSnapshot';

const statusColors = {
  Healthy: 'text-green-400',
  Slowing: 'text-yellow-400',
  'At Risk': 'text-red-400'
};

function StatusBadge({ label }) {
  const colorClass = statusColors[label] || 'text-[#8A93A6]';
  return (
    <Badge variant="outline" className={`${colorClass} border-white/20`}>
      {label || '--'}
    </Badge>
  );
}

export default function AdminGrowth() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('ADMIN GROWTH ACTIVE: frontend/src/pages/new-app/AdminGrowth.jsx');
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    getAppAnalyticsSnapshot()
      .then((data) => {
        if (isMounted) setSnapshot(data);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const flywheelStatus = snapshot?.growth_status || '--';

  const outerLoopStages = snapshot?.outer_loop || [
    { name: 'Top-of-funnel acquisition', status: 'Healthy' },
    { name: 'Lead quality & scoring', status: 'Healthy' },
    { name: 'Sales / onboarding readiness', status: 'Slowing' }
  ];

  const innerLoopStages = snapshot?.inner_loop || [
    { name: 'Activation → first value', status: 'Healthy' },
    { name: 'Content throughput', status: 'Slowing' },
    { name: 'Retention / repeat usage', status: 'At Risk' },
    { name: 'Advocacy / referrals', status: 'Slowing' }
  ];

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-5xl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-5xl">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">Growth / Flywheel</h1>
          <p className="text-[#8A93A6]">Flywheel health (read-only)</p>
        </div>
        <Link to="/app/analyze/app">
          <Button variant="secondary" className="bg-white/10 border border-white/20">
            View canonical metrics in App Analytics
          </Button>
        </Link>
      </div>

      <Card className="glass mb-6">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Status</span>
            <StatusBadge label={flywheelStatus} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[#8A93A6]">
            Read-only signal for governance. Detailed metrics live in App Analytics.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="glass">
          <CardHeader>
            <CardTitle>Outer Loop (company-led acquisition)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {outerLoopStages.map((stage, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10"
                >
                  <span className="text-white">{stage.name}</span>
                  <StatusBadge label={stage.status} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardHeader>
            <CardTitle>Inner Loop (user flywheel)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {innerLoopStages.map((stage, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10"
                >
                  <span className="text-white">{stage.name}</span>
                  <StatusBadge label={stage.status} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
